import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { useMutation, useLazyQuery } from "@apollo/client";
import { LOGIN_MUTATION } from "api/graphql/queries/loginQuery";
import { SELLER_LOGOUT } from "api/graphql/queries/sellerLogoutQuery";
import { GET_SELLER_PROFILE } from "api/graphql/queries/getSellerProfileQuery";
// Seller interface remains the same
interface Seller {
  entity_id: string;
  name: string;
  email: string;
  is_seller: string;
  shop_url: string;
  shop_title: string;
  logo_pic: string;
  company_locality: string;
  contact_number: string;
  billing_full: string;
  billing_firstname: string;
  billing_lastname: string;
  billing_telephone: string;
  billing_postcode: string;
  billing_country_id: string;
  billing_region: string;
  billing_region_id: string;
  billing_street: string;
  billing_city: string;
  billing_company: string;
  [key: string]: any;
}

interface AuthContextProps {
  isAuthenticated: boolean | null;
  seller: Seller | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setSeller: React.Dispatch<React.SetStateAction<Seller | null>>;
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean | null>>;
  profileLoading: boolean;
  loginLoading: boolean;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [seller, setSeller] = useState<Seller | null>(null);

  // Login mutation
  const [sellerLogin, { data: loginData, loading: loginLoading, error: loginError }] = useMutation(LOGIN_MUTATION);


  // Get profile query
  const [getProfile, { loading: profileLoading }] = useLazyQuery(GET_SELLER_PROFILE, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.getSellerProfile?.success) {
        const freshSellerData = data.getSellerProfile.data;
        
        // ALWAYS check for rejected status (is_seller=3), regardless of previous state
        if (freshSellerData.is_seller === "3") {
          console.log("Seller has been rejected. Logging out.");
          localStorage.removeItem("token");
          localStorage.removeItem("seller");
          localStorage.removeItem("sid");
          setSeller(null);
          setIsAuthenticated(false);
          
          // Show an alert before redirecting
          alert("Your seller account has been rejected or deactivated. Please contact support for more information.");
          
          // Redirect to login page
          window.location.href = "/auth/login";
          return;
        }
        
        const storedSellerJson = localStorage.getItem("seller");
        
        if (storedSellerJson) {
          const storedSeller = JSON.parse(storedSellerJson);
          
          // Check if is_seller status has changed
          if (freshSellerData.is_seller && storedSeller.is_seller !== freshSellerData.is_seller) {
            console.log(`Seller status changed from ${storedSeller.is_seller} to ${freshSellerData.is_seller}`);
            
            // Status changed - update stored seller
            const updatedSeller = {
              ...storedSeller,
              is_seller: freshSellerData.is_seller
            };
            
            localStorage.setItem("seller", JSON.stringify(updatedSeller));
            setSeller(updatedSeller);
          } else {
            // No status change, proceed as normal
            setSeller(storedSeller);
          }
        } else {
          // No stored seller data, use the fresh data
          localStorage.setItem("seller", JSON.stringify(freshSellerData));
          setSeller(freshSellerData);
        }
        
        setIsAuthenticated(true);
      } else {
        // Token is invalid or expired
        localStorage.removeItem("token");
        localStorage.removeItem("seller");
        localStorage.removeItem("sid");
        setSeller(null);
        setIsAuthenticated(false);
      }
    },
    onError: () => {
      // Error occurred, token is likely invalid
      localStorage.removeItem("token");
      localStorage.removeItem("seller");
      localStorage.removeItem("sid");
      setSeller(null);
      setIsAuthenticated(false);
    }
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    
    if (token) {
      // Set isAuthenticated to null during loading
      setIsAuthenticated(null);
      // Validate the token by getting the profile
      getProfile();
    } else {
      setIsAuthenticated(false);
      setSeller(null);
    }
  }, [getProfile]);

  // Login function
  const login = async (email: string, password: string) => {
    try {
      console.log("AuthProvider: Starting login process");
      
      // First check if token already exists and clear it to avoid state inconsistencies
      if (localStorage.getItem("token")) {
        localStorage.removeItem("token");
        localStorage.removeItem("seller");
        localStorage.removeItem("sid");
      }
      
      const response = await sellerLogin({
        variables: {
          username: email,
          password,
        },
      });
      
      console.log("AuthProvider: Received login response", response);
      
      if (response?.data?.sellerLogin?.success) {
        // Save token immediately for next API calls
        localStorage.setItem("token", response.data.sellerLogin.token);
        
        // Immediately fetch full profile data - login only returns minimal data now
        try {
          console.log("AuthProvider: Fetching profile data after login");
          const profileResult = await getProfile();
          
          if (profileResult?.data?.getSellerProfile?.success) {
            // Get the is_seller value from login response
            const is_seller = response.data.sellerLogin.seller?.is_seller;
            
            // Create merged data with profile data and is_seller from login
            const mergedData = {
              ...profileResult.data.getSellerProfile.data,
              is_seller: is_seller // Ensure is_seller is included from login response
            };
            
            // Save the merged data to localStorage
            localStorage.setItem("seller", JSON.stringify(mergedData));
            
            // Update state with the merged data
            setSeller(mergedData);
            setIsAuthenticated(true);
          } else {
            // No fallback: Profile fetch is now mandatory
            console.error("AuthProvider: Failed to fetch profile data");
            localStorage.removeItem("token");
            setIsAuthenticated(false);
            throw new Error("Failed to load seller profile data");
          }
        } catch (profileError) {
          console.error("AuthProvider: Error fetching profile after login", profileError);
          localStorage.removeItem("token");
          setIsAuthenticated(false);
          throw new Error("Failed to load seller profile data");
        }
        
        return;
      } else {
        // Handle unsuccessful login with specific message
        const errorMessage = response?.data?.sellerLogin?.message || "Login failed: Invalid credentials";
        
        // Special handling for unauthorized seller accounts (is_seller=3)
        if (errorMessage.includes("not authorized as a seller")) {
          console.error("AuthProvider: Login unsuccessful - Not a seller account");
          throw new Error("This account is not authorized to access the seller portal. Please contact support if you believe this is an error.");
        } else {
          console.error("AuthProvider: Login unsuccessful", errorMessage);
          throw new Error(errorMessage);
        }
      }
    } catch (error: any) {
      console.error("AuthProvider: Login error caught:", error);
      
      // Ensure we're in a clean state
      localStorage.removeItem("token");
      localStorage.removeItem("seller");
      localStorage.removeItem("sid");
      
      // Extract GraphQL error message if available
      const graphQLErrors = error?.graphQLErrors;
      let errorMessage;
      
      if (graphQLErrors && graphQLErrors.length > 0) {
        errorMessage = graphQLErrors[0].message;
      } else if (error.networkError) {
        errorMessage = "Network error: Please check your connection and try again";
      } else {
        errorMessage = error.message || "An unexpected error occurred during login";
      }
      
      console.error("AuthProvider: Throwing error with message:", errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Logout function - frontend only implementation
  const logout = async () => {
    try {
      // Remove data from localStorage
      localStorage.removeItem("token");
      localStorage.removeItem("seller");

      // Clear state
      setSeller(null);
      setIsAuthenticated(false);

        localStorage.removeItem("token");
        localStorage.removeItem("seller");
        localStorage.removeItem("sid");
        setSeller(null);
        setIsAuthenticated(false);

        // window.location.href = "/auth/login";
      
    } catch (error) {
      console.error("Logout failed:", error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        seller,
        login,
        logout,
        setSeller,
        setIsAuthenticated,
        profileLoading,
        loginLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
